import type { FlairSpec } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import type { Part, RibbonKey } from '../body';
import type { Worn } from '../headwear';

/**
 * What the mythic sets share (ADR-0160): each head is a mask over the whole head drawn as one picture function, so the
 * standing head and the lying one (the dead pose: a quarter turn clockwise, the crown to the right) are the same shapes
 * lit from the same top-left, never one resampled into the other.
 */

/** A mythic piece's flair by where it is worn (`flair/index.ts` picks them by what a gardener wears). */
export interface SetFlair {
  head?: FlairSpec;
  outfit?: FlairSpec;
  gun?: FlairSpec;
}

/**
 * A head as a picture: the colour at (u, v) px from the head's middle (u forward, v down, at the standing head's size),
 * lit by a light whose way is (lx, ly) there (unit, pointing at the light); null where it isn't. `lying`: drawn on the
 * dead pose, whose face is bigger than the standing chin (a mask open under its chin or between its crown's points
 * covers it there, a visor shows its dead face).
 */
export type HeadPicture = (u: number, v: number, lx: number, ly: number, lying: boolean) => PaletteKey | null;

/** The standing head's middle in the frame. */
export const HEAD_AT = [30, 28] as const;
/** The lying head's middle in the frame, and its size of the standing one's (the dead face is 32×24). */
const LYING_AT = [42.5, 56.5] as const;
const LYING = 0.74;
/** The light's way in the picture (x right, y down): from the top-left. */
const LIGHT = [-Math.SQRT1_2, -Math.SQRT1_2] as const;

/** `head` standing: over the face in the hair's place (64 × 56 from the frame's top-left). */
export function standing(head: HeadPicture): Part {
  const c = new PixelCanvas(64, 56);
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++)
      c.set(x, y, head(x + 0.5 - HEAD_AT[0], y + 0.5 - HEAD_AT[1], LIGHT[0], LIGHT[1], false));
  return [c, 0, 0];
}

/**
 * `head` on the dead pose (the whole 64 × 72 frame): turned a quarter clockwise (its top to the right, as the dead face
 * lies) and a size smaller, still lit from the picture's top-left. Never under the ground line (the soles' row).
 */
export function lying(head: HeadPicture): Part {
  const c = new PixelCanvas(64, 72);
  for (let y = 0; y < 70; y++)
    for (let x = 0; x < c.w; x++) {
      const [dx, dy] = [(x + 0.5 - LYING_AT[0]) / LYING, (y + 0.5 - LYING_AT[1]) / LYING];
      // The picture's (dx, dy) is the head's (u, v) = (dy, -dx); the light turns with it.
      c.set(x, y, head(dy, -dx, LIGHT[1], -LIGHT[0], true));
    }
  return [c, 0, 0];
}

const NONE: Part = [new PixelCanvas(1, 1), 0, 0];
/** Nothing trails from a mask: what swings behind a mythic gardener is its flair. */
const NO_TAILS: Record<RibbonKey, Part> = { hang: NONE, hang2: NONE, flowA: NONE, flowB: NONE, flowC: NONE };

/** A mask over the whole head from its picture: tied on (it never flies off), the face's extras under it. */
export const mask = (head: HeadPicture): Worn => ({
  worn: true,
  front: standing(head),
  ribbon: NO_TAILS,
  dead: lying(head),
  mask: true,
});

export type Ellipse = readonly [cx: number, cy: number, rx: number, ry: number];
/** Where (x, y) is in `e`: under 1 inside, 1 on its edge. */
export const inEllipse = (x: number, y: number, [cx, cy, rx, ry]: Ellipse): number =>
  ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;

/**
 * The light on a dome at (x, y) of `e` (its outline), bulging toward us: its normal's dot with a light from (lx, ly) and
 * a little in front, so the brightest spot sits a third of the way in from the lit rim; 1 there, under 0 turned away.
 */
export function domeLight(x: number, y: number, [cx, cy, rx, ry]: Ellipse, lx: number, ly: number): number {
  const [nx, ny] = [(x - cx) / rx, (y - cy) / ry];
  const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
  return (nx * lx + ny * ly) * 0.737 + nz * 0.676;
}

/** The tone for light `l`: the first of `steps` ([tone, at least this lit], brightest first) it reaches, else `dark`. */
export function toneOf(l: number, steps: readonly (readonly [PaletteKey, number])[], dark: PaletteKey): PaletteKey {
  for (const [tone, from] of steps) if (l >= from) return tone;
  return dark;
}

/** How far (x, y) is from the segment (x0, y0)-(x1, y1). */
export function fromSegment(x: number, y: number, x0: number, y0: number, x1: number, y1: number): number {
  const [dx, dy] = [x1 - x0, y1 - y0];
  const t = Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(x - x0 - dx * t, y - y0 - dy * t);
}

/** How far (x, y) is from the path through `pts` (x, y pairs). */
export function fromPath(x: number, y: number, pts: readonly number[]): number {
  let d = Infinity;
  for (let i = 0; i + 3 < pts.length; i += 2)
    d = Math.min(
      d,
      fromSegment(x, y, pts[i] as number, pts[i + 1] as number, pts[i + 2] as number, pts[i + 3] as number),
    );
  return d;
}
